/**
 * Utility functions for handling skills from query strings and localStorage
 */
import axios from "axios";

/**
 * Parse skills from URL query string
 * @param {string} url - The URL containing skills query parameter
 * @returns {Array<string>} Array of skill names
 */
export const parseSkillsFromUrl = (url = window.location.href) => {
  try {
    const urlObj = new URL(url);
    const skillsParam = urlObj.searchParams.get("skills");

    if (!skillsParam) {
      return [];
    }

    // Decode and split skills by comma
    const decodedSkills = decodeURIComponent(skillsParam);
    const skills = decodedSkills
      .split(",")
      .map((skill) => skill.trim())
      .filter((skill) => skill.length > 0);

    return skills;
  } catch (error) {
    console.error("Error parsing skills from URL:", error);
    return [];
  }
};

/**
 * Store skills in localStorage with timestamp
 * @param {Array<string>} skills - Array of skill names to store
 * @param {string} source - Source of the redirect (default: "external-redirect")
 */
export const storeSkillsInLocalStorage = (skills, source = "external-redirect") => {
  try {
    if (skills && skills.length > 0) {
      const skillsData = {
        skills: skills,
        timestamp: new Date().toISOString(),
        source: source,
      };

      localStorage.setItem("pendingSkills", JSON.stringify(skillsData));
      // console.log("Skills stored in localStorage:", skillsData);
    }
  } catch (error) {
    console.error("Error storing skills in localStorage:", error);
  }
};

/**
 * Retrieve skills from localStorage
 * @returns {Array<string>} Array of skill names or empty array if none found
 */
export const getSkillsFromLocalStorage = () => {
  try {
    const storedData = localStorage.getItem("pendingSkills");

    if (storedData) {
      const skillsData = JSON.parse(storedData);

      // Check if data is not too old (24 hours)
      const storedTime = new Date(skillsData.timestamp);
      const now = new Date();
      const hoursDiff = (now - storedTime) / (1000 * 60 * 60);

      if (hoursDiff < 24 && skillsData.skills && skillsData.skills.length > 0) {
        return skillsData.skills;
      } else {
        // Remove expired data
        localStorage.removeItem("pendingSkills");
      }
    }

    return [];
  } catch (error) {
    console.error("Error retrieving skills from localStorage:", error);
    return [];
  }
};

/**
 * Remove skills from localStorage
 */
export const clearSkillsFromLocalStorage = () => {
  try {
    localStorage.removeItem("pendingSkills");
    // console.log("Skills cleared from localStorage");
  } catch (error) {
    console.error("Error clearing skills from localStorage:", error);
  }
};

/**
 * Check if there are pending skills in localStorage
 * @returns {boolean} True if there are pending skills
 */
export const hasPendingSkills = () => {
  const skills = getSkillsFromLocalStorage();
  return skills.length > 0;
};

/**
 * Convert skills array to the format expected by TechRelevanceForm
 * If skills are already full objects with validation data, return them as-is
 * If skills are just names (strings or objects with only name), validate them via API
 * @param {Array<string|Object>} skills - Array of skill names or skill objects
 * @param {Function} [DataEncryption] - Optional function to encrypt data for API
 * @param {Function} [DataDecryption] - Optional function to decrypt API response
 * @returns {Promise<Array<Object>>} Array of validated skill objects
 */
export const formatSkillsForForm = async (skills, DataEncryption, DataDecryption) => {
  if (!skills || skills.length === 0) {
    return [];
  }

  // Check if skills already have full validation data (have estYear, expiryYear, score, etc.)
  const hasFullValidationData = skills.every((skill) => {
    const skillObj = typeof skill === 'string' ? { name: skill } : skill;
    return (
      skillObj.name &&
      (skillObj.estYear !== undefined ||
        skillObj.expiryYear !== undefined ||
        skillObj.score !== undefined ||
        skillObj.skillType !== undefined)
    );
  });

  // If skills already have validation data, return them as-is
  if (hasFullValidationData) {
    return skills.map((skill) => (typeof skill === 'string' ? { name: skill } : skill));
  }

  // Skills need validation - format them for API call
  const skillsToValidate = skills.map((skill) => {
    if (typeof skill === 'string') {
      return { name: skill };
    }
    return skill;
  });

  // If DataEncryption and DataDecryption are not provided, return formatted skills without validation
  if (!DataEncryption || !DataDecryption) {
    return skillsToValidate;
  }

  // Call validation API
  try {
    const response = await axios.post(
      process.env.REACT_APP_API_BASE_URL + "/get-topSkillValidity",
      DataEncryption({
        primarySkill: skillsToValidate,
        type: "skill-relevance",
      }),
      {
        options: {
          type: "json",
        },
      }
    );

    const data = DataDecryption(response.data);

    // Handle duplicates - filter them out
    const validatedSkills = data.topPrimarySkill.filter(
      (item) => item.isDuplicate === false
    );

    return validatedSkills;
  } catch (error) {
    console.error("Error validating skills in formatSkillsForForm:", error);
    // On error, return skills formatted with just name property
    return skillsToValidate;
  }
};

/**
 * Clear URL parameters (remove skills and auth parameters from URL)
 */
export const clearUrlParameters = () => {
  try {
    const url = new URL(window.location);

    // Remove skills parameter
    url.searchParams.delete("skills");

    // Remove OAuth callback parameters
    url.searchParams.delete("code");
    url.searchParams.delete("state");

    // Remove any other common auth parameters
    url.searchParams.delete("error");
    url.searchParams.delete("error_description");

    // Update URL without reloading the page
    window.history.replaceState({}, document.title, url.pathname + url.search);
    // console.log("URL parameters cleared:", url.pathname + url.search);
  } catch (error) {
    console.error("Error clearing URL parameters:", error);
  }
};

/**
 * Store relevance result data in sessionStorage (for transfer from home page to skill-relevance page)
 * @param {object} relevanceData - The relevance result data object
 */
export const storeRelevanceResult = (relevanceData) => {
  try {
    if (relevanceData) {
      const relevanceDataWithTimestamp = {
        data: relevanceData,
        timestamp: new Date().toISOString(),
        source: "homepage-check",
      };
      sessionStorage.setItem("pendingRelevanceResult", JSON.stringify(relevanceDataWithTimestamp));
      // console.log("Relevance result stored in sessionStorage");
    }
  } catch (error) {
    console.error("Error storing relevance result:", error);
  }
};

/**
 * Retrieve relevance result data from sessionStorage
 * @returns {object|null} Relevance result data or null if not found/expired
 */
export const getRelevanceResult = () => {
  try {
    const storedData = sessionStorage.getItem("pendingRelevanceResult");

    if (storedData) {
      const relevanceData = JSON.parse(storedData);

      // Check if data is not too old (1 hour)
      const storedTime = new Date(relevanceData.timestamp);
      const now = new Date();
      const hoursDiff = (now - storedTime) / (1000 * 60 * 60);

      if (hoursDiff < 1 && relevanceData.data) {
        return relevanceData.data;
      } else {
        // Remove expired data
        sessionStorage.removeItem("pendingRelevanceResult");
      }
    }

    return null;
  } catch (error) {
    console.error("Error retrieving relevance result:", error);
    return null;
  }
};

/**
 * Clear relevance result data from sessionStorage
 */
export const clearRelevanceResult = () => {
  try {
    sessionStorage.removeItem("pendingRelevanceResult");
    // console.log("Relevance result cleared from sessionStorage");
  } catch (error) {
    console.error("Error clearing relevance result:", error);
  }
};

/**
 * Check if there is pending relevance result data
 * @returns {boolean} True if there is pending relevance result
 */
export const hasPendingRelevanceResult = () => {
  const result = getRelevanceResult();
  return result !== null;
};